import { apiBase } from './api';

export interface Account {
  customerId: number;
  email: string;
  name: string | null;
  subscription: { tier: 'free' | 'pro'; status: string; billingCycle: string | null; subscriptionEndsAt: string | null };
  /**
   * 'viewer' for a session opened by a share link.
   *
   * Used to hide what a viewer cannot do. It is NOT the boundary: the server
   * refuses the write whatever this page decided to draw.
   */
  kind: 'owner' | 'viewer';
}

// ── Index evidence: which pages an AI index has actually shown ───────────────

export interface PageIndexEvidence {
  url: string;
  index: 'Bing' | 'Google';
  lastSeenAt: string;
  timesCited: number;
  exampleQuery: string | null;
}
export interface IndexEvidence {
  domain: string;
  pages: PageIndexEvidence[];
  domainSeenIn: { index: 'Bing' | 'Google'; lastSeenAt: string }[];
  runs: number;
  summary: string[];
}

/**
 * Proof, never absence.
 *
 * A citation to your own URL is the search engine showing it had that page
 * indexed. A page missing from this list has not been cited yet, which is not
 * the same as not being indexed, and the copy says so.
 */
export const getIndexEvidence = (domain: string) =>
  get<IndexEvidence>(`/account/index-evidence?domain=${encodeURIComponent(domain)}`);

// ── Read-only share links, for agencies ──────────────────────────────────────

export interface ShareLink {
  id: number;
  label: string;
  domain: string | null;
  createdAt: string;
  expiresAt: string | null;
  revokedAt: string | null;
  lastSeenAt: string | null;
  useCount: number;
  state: 'active' | 'revoked' | 'expired';
}

export const getShareLinks = () =>
  get<{ links: ShareLink[] }>('/account/share-links').then((d) => d.links);

/** The URL comes back once and is not recoverable. Show it, then it is gone. */
export const createShareLink = (input: { label: string; domain?: string | null; days?: number | null }) =>
  post<{ link: ShareLink; url: string }>('/account/share-links', input);

export const revokeShareLink = (id: number) =>
  del<{ revoked: boolean }>(`/account/share-links/${id}`);
export interface OrderInfo {
  orderId: number;
  email: string;
  cycle: string;
  amountEur: string;
}
export interface Usage {
  yearMonth: string;
  scan_count: number;
  probe_count: number;
  cost_cents: number;
  priceEur: string;
}

type Env<T> = { ok: true; data: T } | { ok: false; error: string };

/**
 * A 401 anywhere means the session is gone, so say so once, loudly, in one place.
 *
 * Without this every panel that happens to be mounted fires its own request,
 * each one 401s, and the page carries on looking signed in while the console
 * fills with failures. That is what a dead session looked like: eight errors and
 * no explanation. The provider listens for this and re-reads /me, which answers
 * `account: null` and flips the UI to signed out.
 *
 * An event rather than a callback so it works from every caller without
 * threading a handler through twenty components.
 */
export const SESSION_LOST = 'aivs:session-lost';

function noteUnauthorised(status: number): void {
  if (status === 401 && typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(SESSION_LOST));
  }
}

/**
 * An API failure that still knows what the server said.
 *
 * A bare Error loses the status, and the caller that needs it is the session: a
 * 401 means signed out, while a network blip means try again later and must NOT
 * sign anybody out.
 */
export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
    this.name = 'ApiError';
  }
}

async function post<T>(path: string, body?: unknown): Promise<T> {
  // Only set Content-Type when there's a body — otherwise Fastify 400s on the
  // empty JSON body (this is what broke logout, which has no body).
  const res = await fetch(`${apiBase}${path}`, {
    method: 'POST',
    credentials: 'include',
    ...(body ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) } : {}),
  });
  const json = (await res.json()) as Env<T>;
  if (!json.ok) { noteUnauthorised(res.status); throw new ApiError(json.error, res.status); }
  return json.data;
}

async function put<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${apiBase}${path}`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = (await res.json()) as Env<T>;
  if (!json.ok) { noteUnauthorised(res.status); throw new ApiError(json.error, res.status); }
  return json.data;
}

async function del<T>(path: string): Promise<T> {
  const res = await fetch(`${apiBase}${path}`, { method: 'DELETE', credentials: 'include' });
  const json = (await res.json()) as Env<T>;
  if (!json.ok) { noteUnauthorised(res.status); throw new ApiError(json.error, res.status); }
  return json.data;
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`${apiBase}${path}`, { credentials: 'include' });
  // A proxy or an error page answers with HTML, and res.json() then throws
  // something that says nothing useful. Status first, body second.
  let json: Env<T>;
  try {
    json = (await res.json()) as Env<T>;
  } catch {
    throw new ApiError(`server returned ${res.status}`, res.status);
  }
  if (!json.ok) { noteUnauthorised(res.status); throw new ApiError(json.error, res.status); }
  return json.data;
}

export interface HistoryRow {
  id: string; createdAt: string; domain: string;
  kind: 'readiness' | 'visibility' | 'footprint';
  readiness: number | null; total: number | null; mentioned: number | null; cited: number | null; knows: boolean | null;
  engine: string | null;
}
export interface HistoryPage { rows: HistoryRow[]; total: number; page: number; pageSize: number; domains: string[] }
export interface EngineStat {
  engine: 'openai' | 'gemini';
  visibility: { mentioned: number; cited: number; total: number; at: string } | null;
  footprint: { mentioned: number; cited: number; total: number; knows: boolean | null; at: string } | null;
}

export const getMe = () => get<{ account: Account | null }>('/account/me').then((d) => d.account);
export const getAccountHistory = (opts: { page: number; domain?: string; kind?: string; engine?: string; from?: string; to?: string }) => {
  const p = new URLSearchParams({ page: String(opts.page) });
  if (opts.domain) p.set('domain', opts.domain);
  if (opts.kind) p.set('kind', opts.kind);
  if (opts.engine) p.set('engine', opts.engine);
  if (opts.from) p.set('from', opts.from);
  if (opts.to) p.set('to', opts.to);
  return get<HistoryPage>(`/account/history?${p.toString()}`);
};
export const getAccountEngineComparison = (domain?: string) =>
  get<{ engines: EngineStat[] }>(`/account/engine-comparison${domain ? `?domain=${encodeURIComponent(domain)}` : ''}`).then((d) => d.engines);
export const requestLink = (email: string) => post<{ sent: boolean; devLink?: string }>('/account/request-link', { email });
// ── Scan runs (#3b): 1 record = 1 full scan ──
export interface ScanRun {
  id: string; createdAt: string; domain: string; url: string; engine: string;
  readinessScore: number | null; readinessEngine: string | null;
  visTotal: number | null; visMentioned: number | null; visCited: number | null;
  fpKnows: string | null; fpTotal: number | null; fpMentioned: number | null; fpCited: number | null;
  /** Fingerprint of the questions this run asked — the trend can only be
   * compared across runs that share it (null on runs stored before D-30). */
  questionSet?: string | null;
  /** The named question set this run asked, snapshotted at run time (null on
   * runs stored before 015, and on the free 5-question path which has no set). */
  querySetId?: string | null;
  querySetName?: string | null;
}
export interface ScanRunPage { rows: ScanRun[]; total: number; page: number; pageSize: number; domains: string[]; querySets?: string[] }

/** How one named question set has performed across the runs that used it. */
export interface QuerySetStat {
  name: string; runs: number; domains: string[]; engines: string[];
  firstAt: string; lastAt: string;
  avgQuestions: number | null; avgMentioned: number | null; avgCited: number | null;
  namedPct: number | null; latestPct: number | null; trendPct: number | null;
}
export interface RecoverableSet {
  name: string; domain: string; queries: string[]; runs: number; lastUsedAt: string;
}

/** Sets this account measured with that are no longer saved. */
export const getRecoverableSets = (domain?: string) =>
  get<{ sets: RecoverableSet[] }>(`/account/query-sets/recoverable${domain ? `?domain=${encodeURIComponent(domain)}` : ''}`)
    .then((d) => d.sets);

/** Rebuild one, exactly as the run asked it. The server takes the name, not the questions. */
export const restoreQuerySet = (name: string, domain: string) =>
  post<{ set: QuerySet; restored: number }>('/account/query-sets/restore', { name, domain });

export interface SetTrendPoint {
  at: string; runId: string; engine: string;
  asked: number; mentioned: number; cited: number; namedPct: number;
}
export interface SetQuestion {
  query: string;
  latest: Record<string, string>;
  /** Every asking of this question, oldest first. */
  points: { at: string; engine: string; state: string }[];
  wins: number;
  asked: number;
}
export interface SetTrend {
  name: string; domain: string;
  points: SetTrendPoint[];
  questions: SetQuestion[];
  saved: boolean;
}

/** One set over time and question by question. Oldest point first. */
export const getSetTrends = (opts: { domain?: string; name?: string; engine?: string; from?: string; to?: string } = {}) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(opts)) if (v) p.set(k, v);
  return get<{ sets: SetTrend[] }>(`/account/set-trends${p.toString() ? `?${p}` : ''}`).then((d) => d.sets);
};

export interface SetAnalysis {
  id: string; domain: string; setName: string; engine: string;
  latestPct: number | null; previousPct: number | null; deltaPct: number | null;
  summary: string;
  findings: { text: string; facts: string[] }[];
  actions: { text: string; why: string }[];
  dropped: { text: string; reason: string }[];
  model: string | null; createdAt: string;
  /**
   * 'ok' or 'empty'. An empty run produced nothing the measurements support,
   * and it is returned rather than hidden so the screen can say that happened
   * instead of showing a blank panel after somebody waited for it.
   */
  status?: string;
}

/** Ask why a set got worse. Returns immediately; the work happens in the background. */
export const askSetAnalysis = (domain: string, name: string, engine: 'gemini' | 'openai') =>
  post<{ started: boolean }>('/account/set-analysis', { domain, name, engine });

export const getSetAnalysis = (domain: string, name: string, engine?: string) => {
  const p = new URLSearchParams({ domain, name });
  if (engine) p.set('engine', engine);
  return get<{ analysis: SetAnalysis | null; canAsk: boolean; why: string | null }>(`/account/set-analysis?${p}`);
};

export interface HiddenSet { domain: string; name: string; hiddenAt: string }

/** Sets kept out of the reports. Hidden, not deleted — the runs keep their data. */
export const getHiddenSets = (domain?: string) =>
  get<{ hidden: HiddenSet[] }>(`/account/query-sets/hidden${domain ? `?domain=${encodeURIComponent(domain)}` : ''}`)
    .then((d) => d.hidden);

export const setQuerySetHidden = (name: string, domain: string, hidden: boolean) =>
  post<{ hidden: HiddenSet[] }>('/account/query-sets/hidden', { name, domain, hidden }).then((d) => d.hidden);

export const getQuerySetStats = (q: { domain?: string; engine?: string; from?: string; to?: string }) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) if (v) p.set(k, v);
  return get<{ stats: QuerySetStat[] }>(`/account/query-set-stats?${p.toString()}`).then((d) => d.stats);
};
export interface ScanRunDetail extends ScanRun { detail: { readiness: unknown; visibility: unknown[]; footprint: unknown } }

export const saveScanRun = (payload: {
  url: string; engine: string; readinessScore: number | null; readinessEngine: string | null;
  readinessFull: unknown; visibility: unknown[]; footprint: unknown;
  /** Which named question set was asked — snapshotted onto the run. */
  querySetId?: string | null; querySetName?: string | null;
}) => post<{ id: number }>('/account/scan-runs', payload);

// ── Who AI names instead of you, over time (#1/#2) ──
export interface RivalTrend {
  name: string;
  points: { at: string; runId: string; total: number; count: number }[];
  latestShare: number;
  firstSeen: string;
}
export interface SourceCount { host: string; queries: number }
export interface CompetitiveView {
  runs: number;
  rivals: RivalTrend[];
  /** Domains cited on the questions you LOSE — the pages beating you. */
  lossSources: SourceCount[];
  /** Domains cited on the questions you win. */
  /** Your own pages, cited where AI named you. */
  winSources: SourceCount[];
  /** Cited in the same answers and NOT yours — often just name-alikes. */
  coSources: SourceCount[];
}
export const getCompetitive = (opts: { domain?: string; engine?: string; from?: string; to?: string; querySet?: string } = {}) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(opts)) if (v) p.set(k, v);
  return get<CompetitiveView>(`/account/competitive${p.toString() ? `?${p}` : ''}`);
};

// ── Automatic re-checks (D-30) — the client's own switch ──
export interface Schedule {
  id: string; domain: string; url: string; engine: 'openai' | 'gemini';
  enabled: boolean; frequency: 'weekly' | 'monthly';
  nextRunAt: string; lastRunAt: string | null; lastError: string | null;
  /** The question set it re-runs; null only on rows from before per-set schedules. */
  querySetId: string | null; querySetName: string | null;
}
export const getSchedule = (url: string) =>
  get<{ schedule: Schedule | null }>(`/account/schedules?url=${encodeURIComponent(url)}`).then((d) => d.schedule);
export const listSchedules = () => get<{ schedules: Schedule[] }>('/account/schedules').then((d) => d.schedules);
export const getSetSchedule = (querySetId: string) =>
  get<{ schedule: Schedule | null }>(`/account/schedules?querySetId=${encodeURIComponent(querySetId)}`).then((d) => d.schedule);
export const setSchedule = (payload: { url: string; enabled: boolean; engine?: string; frequency?: 'weekly' | 'monthly'; querySetId?: number }) =>
  put<{ schedule: Schedule }>('/account/schedules', payload).then((d) => d.schedule);

export const getAccountScanRuns = (opts: { page: number; domain?: string; engine?: string; from?: string; to?: string; querySet?: string }) => {
  const p = new URLSearchParams({ page: String(opts.page) });
  if (opts.domain) p.set('domain', opts.domain);
  if (opts.engine) p.set('engine', opts.engine);
  if (opts.from) p.set('from', opts.from);
  if (opts.to) p.set('to', opts.to);
  if (opts.querySet) p.set('querySet', opts.querySet);
  return get<ScanRunPage>(`/account/scan-runs?${p.toString()}`);
};
export const getScanRunDetail = (id: string) => get<{ run: ScanRunDetail }>(`/account/scan-runs/${id}`).then((d) => d.run);
export const deleteScanRun = (id: string) => del<{ ok: true }>(`/account/scan-runs/${id}`);
export const getRunsComparison = (domain?: string) =>
  get<{ runs: ScanRun[] }>(`/account/runs-comparison${domain ? `?domain=${encodeURIComponent(domain)}` : ''}`).then((d) => d.runs);

export const order = (cycle: 'monthly' | 'annual') => post<OrderInfo>('/account/order', { cycle });
export const getUsage = () => get<Usage>('/account/usage');
export const logout = () => post<{ loggedOut: boolean }>('/account/logout');

// ── Question sets (D-33/D-34) — named, several per domain, one active ──
export interface QuerySet {
  id: string; name: string; domain: string; queries: string[];
  source: 'generated' | 'approved'; isActive: boolean; updatedAt: string;
}
export const listQuerySets = (url?: string) =>
  get<{ sets: QuerySet[]; active?: QuerySet | null }>(`/account/query-sets${url ? `?url=${encodeURIComponent(url)}` : ''}`);
export const saveQuerySetFor = (body: { id?: number; url: string; name?: string; queries: string[]; activate?: boolean }) =>
  put<{ set: QuerySet; dropped: number }>('/account/query-sets', body);
export const activateQuerySet = (id: string) =>
  post<{ set: QuerySet }>(`/account/query-sets/${id}/activate`);
export const deleteQuerySet = (id: string) =>
  del<{ ok: boolean }>(`/account/query-sets/${id}`);

// ── Per-question history (D-34) ──
export interface QuestionTrend {
  query: string;
  points: { at: string; engine: string; state: string; runId: string }[];
  latest: Record<string, string>;
  /** The sets that ask this question, most recently used first. */
  sets: string[];
  wins: number;
  asked: number;
}
export const getQuestionTrends = (opts: { domain?: string; engine?: string; from?: string; to?: string; querySet?: string } = {}) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(opts)) if (v) p.set(k, v);
  return get<{ runs: number; questions: QuestionTrend[] }>(`/account/question-trends${p.toString() ? `?${p}` : ''}`);
};

// ── "I changed that page" → did it work? ─────────────────────────────────────

export interface FixOutcome {
  query: string; pageUrl: string | null; stateBefore: string; note: string | null;
  markedAt: string; stateNow: string | null;
  verdict: 'improved' | 'unchanged' | 'worse' | 'not-re-measured';
}
export const getFixOutcomes = (domain: string, querySet?: string) =>
  get<{ outcomes: FixOutcome[] }>(
    `/account/fix-marks?domain=${encodeURIComponent(domain)}${querySet ? `&querySet=${encodeURIComponent(querySet)}` : ''}`,
  ).then((d) => d.outcomes);
export const markFixed = (b: { domain: string; query: string; pageUrl?: string | null; stateBefore: string; note?: string | null }) =>
  post<{ ok: true }>('/account/fix-marks', b);
export const unmarkFixed = (domain: string, query: string) =>
  del<{ ok: true }>(`/account/fix-marks?domain=${encodeURIComponent(domain)}&query=${encodeURIComponent(query)}`);

export interface EyePluginDownload {
  version: string; size: number; sha256: string;
  requiresWp: string; requiresPhp: string;
  url: string; expiresAt: string;
}

/**
 * Mint a one-time link to the AI EYE plugin zip.
 *
 * A POST because it creates something — a link that works once and expires —
 * rather than reading a fixed URL. Pro-only, enforced on the server.
 */
export const requestEyePlugin = () => post<EyePluginDownload>('/account/eye-plugin', {});
